import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { arrayOf, oneOfType, node, string, func, oneOf } from 'prop-types';
import { getMaskStyle } from 'components/styles';

import ReactHtmlParser from 'react-html-parser';

// Import utilities
import useToggle from 'components/utilities/hooks/toggle/useToggle';

// Import helpers
import { callAllFns } from 'helpers';
import { GEO_LOCK_MODAL_TYPES, VERIFICATION } from 'helpers/variables';

// Import styles
import {
	ModalStyled,
	TextStyled,
	ModalContentWrapper,
	ModalContentVerificationWrapper
} from './styles';

// Import components
import useGeoLockModal from './useGeoLockModal';
import ModalContent from './ModalContent';
import ApprovalModal from 'components/elements/molecules/approval_modal/ApprovalModal';

const GeoLockModal = ({
	children,
	modalTitle,
	modalDescription,
	modalButtonText,
	onModalClose,
	onModalAccept,
	acceptButtonText,
	rejectButtonText,
	type,
	onClick
}) => {
	const { t } = useTranslation();

	const { identity_verification_state: userVerificationState } = useSelector(
		({ auth }) => auth.authenticate || {}
	);

	const { block, pass, isLoading, isEOG } = useGeoLockModal(
		type,
		userVerificationState
	);

	const [open, toggle] = useToggle();

	useEffect(() => {
		if (!children) {
			!isLoading && block(toggle)();
		}

		// eslint-disable-next-line
	}, [isLoading]);

	const renderModal = () => {
		switch (type) {
			case GEO_LOCK_MODAL_TYPES.PURCHASE:
				return (
					<ModalStyled
						maskClosable={false}
						width="64rem"
						closable={true}
						onCancel={callAllFns(onModalClose, toggle)}
						visible={open}
						bodyStyle={{ padding: '5rem 2rem' }}
					>
						<ModalContent
							title={modalTitle}
							description={modalDescription}
							buttonText={modalButtonText}
							toggle={callAllFns(onModalClose, toggle)}
						/>
					</ModalStyled>
				);

			case GEO_LOCK_MODAL_TYPES.WATCH: {
				if (isEOG) {
					if (userVerificationState === VERIFICATION.IN_PROGRESS) {
						return (
							<ModalStyled
								maskClosable={false}
								closable={false}
								width={'64rem'}
								onCancel={callAllFns(onModalClose, toggle)}
								visible={open}
								bodyStyle={{ padding: '5rem 2rem' }}
							>
								<ModalContentVerificationWrapper>
									<ModalContent
										title={'geolock_watching_verification_in_progress_title'}
										description={
											'geolock_watching_verification_in_progress_description'
										}
										buttonText={'common_general_ok'}
										toggle={callAllFns(onModalClose, toggle)}
									/>
								</ModalContentVerificationWrapper>
							</ModalStyled>
						);
					}

					if (userVerificationState === VERIFICATION.VERIFIED) {
						return null;
					}

					return (
						<ApprovalModal
							isVisible={open}
							onReject={onModalAccept}
							title={modalTitle}
							onAccept={callAllFns(onModalClose, toggle)}
							acceptButtonText={acceptButtonText}
							rejectButtonText={rejectButtonText}
							showMask={getMaskStyle('primary')}
						>
							<TextStyled>{ReactHtmlParser(t(modalDescription))}</TextStyled>
						</ApprovalModal>
					);
				} else {
					return (
						<ModalStyled
							maskClosable={false}
							closable={false}
							width={'64rem'}
							onCancel={callAllFns(onModalClose, toggle)}
							visible={open}
							bodyStyle={{ padding: '5rem 2rem' }}
							mask={true}
							maskStyle={getMaskStyle('primary')}
						>
							<ModalContentWrapper>
								<ModalContent
									title={'verification_modal_outside_eog'}
									buttonText={'geolock_watching_ok'}
									toggle={callAllFns(onModalClose, toggle)}
								/>
							</ModalContentWrapper>
						</ModalStyled>
					);
				}
			}
			default:
				return null;
		}
	};

	if (!children) {
		return renderModal();
	}

	return (
		<>
			{React.cloneElement(children, {
				onClick: callAllFns(
					onClick,
					block(toggle),
					pass(children?.props?.onClick)
				),
				disabled: isLoading
			})}
			{open && renderModal()}
		</>
	);
};

GeoLockModal.defaultProps = {
	type: GEO_LOCK_MODAL_TYPES.PURCHASE,
	acceptButtonText: 'geolock_watching_cancel_btn',
	rejectButtonText: 'geolock_watching_add_scan_btn'
};

GeoLockModal.propTypes = {
	children: oneOfType([node, arrayOf(node)]),
	modalTitle: string,
	modalDescription: string,
	modalButtonText: string,
	onModalClose: func,
	onModalAccept: func,
	type: oneOf(Object.values(GEO_LOCK_MODAL_TYPES))
};

export default GeoLockModal;
